<template>
    <div>

        <p v-if="selected.length === 0" class="text-muted mb-0">
            Tick the jobs you want to deal with. Every job on this screen has had all its
            reminders, so any of them can be actioned.
        </p>

        <div v-else>

            <ul class="selectedList list-inline mb-2">
                <li class="list-inline-item">
                    <strong>Checked:</strong>
                </li>
                <li v-for="job in selected" :key="job.id" class="list-inline-item">
                    <span class="badge bg-danger">
                        {{ job.reference }}
                    </span>
                </li>
                <li class="list-inline-item">
                    <a href="#" @click.prevent="clearSelection()">Clear?</a>
                </li>
            </ul>

            <!-- Stated before the button as well as inside the confirmation: the
                 irreversible action has to be visible without opening a dialog to find
                 it. No breakdown by stage, unlike the PO Manager — every row here is at
                 the same point, so there is only ever one thing this does. -->
            <p class="mb-3">
                <strong>{{ selected.length }} {{ selected.length === 1 ? 'job' : 'jobs' }}</strong>
                will be invoiced with <strong>no purchase order</strong>, and the
                {{ selected.length === 1 ? 'invoice' : 'invoices' }} emailed immediately.
            </p>

            <button
                type="button"
                class="btn btn-sm btn-danger"
                :disabled="working"
                @click="confirmThenInvoice()"
            >
                <span v-if="working">
                    <i class="fa fa-spinner fa-spin me-1"></i> Working&hellip;
                </span>
                <span v-else>{{ actionSummary }}</span>
            </button>

            <button
                type="button"
                class="btn btn-sm btn-outline-secondary ms-2"
                :disabled="working || referenceWorking"
                @click="confirmThenSetReference(selected, reload)"
            >
                <span v-if="referenceWorking">
                    <i class="fa fa-spinner fa-spin me-1"></i> Working&hellip;
                </span>
                <span v-else>Set PO &amp; Invoice</span>
            </button>

        </div>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { usePoReference } from '@/composables/usePoReference';

const TABLE = '#datatable-invoicing-list';

const selected = ref([]);
const working = ref(false);
const { confirmThenSetReference, working: referenceWorking } = usePoReference();

const actionSummary = computed(() => {
    const total = selected.value.length;

    return `Invoice ${total} ${total === 1 ? 'Job' : 'Jobs'} Without PO`;
});

/**
 * The selection is **read from the checkboxes**, never accumulated alongside
 * them.
 *
 * Adding on tick and filtering on untick looks equivalent and is not: select-all
 * fires a change for every box including the ones already ticked, so an
 * accumulating list counts those jobs twice — visible as a repeated reference in
 * the badges, and fatal at submit time, where duplicate ids are rejected
 * outright by the `distinct` rule on PoBulkActionRequest. That defect was found
 * on the PO Manager during manual testing; this screen is built the same way so
 * as not to reintroduce it.
 */
const syncFromCheckboxes = () => {
    const checked = [];

    window.jQuery(`${TABLE} .bulk-update-checkbox:checked`).each((_, el) => {
        checked.push(JSON.parse(el.value));
    });

    selected.value = checked;
};

/**
 * Delegated from the document rather than bound to the checkboxes or to the
 * DataTables instance: the table replaces every row on every draw, and this
 * component mounts alongside List.vue, so reaching for the instance here would
 * race the component that creates it.
 *
 * Namespaced per screen so that unmounting this one cannot tear off the PO
 * Manager's handlers.
 */
onMounted(() => {
    window.jQuery(document).on('change.invoicingBulk', `${TABLE} .bulk-update-checkbox`, syncFromCheckboxes);
    window.jQuery(document).on('draw.dt.invoicingBulk', TABLE, syncFromCheckboxes);
});

onUnmounted(() => {
    window.jQuery(document).off('change.invoicingBulk');
    window.jQuery(document).off('draw.dt.invoicingBulk');
});

const clearSelection = () => {
    window.jQuery(`${TABLE} .bulk-update-checkbox:checked`).each((_, el) => {
        el.checked = false;
    });

    window.jQuery('#invoicing-check-all').prop('checked', false);

    syncFromCheckboxes();
};

const confirmThenInvoice = () => {
    if (working.value || selected.value.length === 0) {
        return;
    }

    const total = selected.value.length;
    const references = selected.value
        .map((job) => `<strong>${job.reference}</strong>`)
        .join(', ');

    Swal.fire({
        icon: 'warning',
        title: `${actionSummary.value}?`,
        html: `<p class="mb-2">This raises an invoice for ${references} with `
            + '<strong>no purchase order on it</strong>, and emails '
            + `${total === 1 ? 'it' : 'them'} to the ${total === 1 ? 'customer' : 'customers'} `
            + 'immediately.</p>'
            + '<p class="mb-0">Every one of these customers was asked twice and did not supply '
            + 'a reference. This cannot be undone.</p>',
        showCancelButton: true,
        confirmButtonText: `Yes, invoice ${total} ${total === 1 ? 'job' : 'jobs'}`,
        confirmButtonColor: '#dc3545',
        cancelButtonText: 'Cancel',
        width: '650px',
    }).then((result) => {
        if (result.isConfirmed) {
            process();
        }
    });
};

/**
 * The same endpoint as a single press — a per-row press is a selection of one —
 * so eligibility is re-applied server side for every id, and a screen left open
 * since before a customer answered cannot force an action the service refuses.
 */
const process = () => {
    working.value = true;

    const data = new FormData();
    data.append('ids', selected.value.map((job) => job.id).join(','));
    // This screen invoices; it does not chase (Stage 17). A job whose reminder
    // count has been reset since the table was drawn is refused rather than
    // quietly sent another email.
    data.append('intent', 'invoice-without-po');

    axios.post(route('api.movements.po.process'), data)
        .then((response) => {
            working.value = false;
            reportOutcome(response.data);
        })
        .catch((error) => {
            working.value = false;
            reportError(error);
        });
};

/**
 * Always a modal, never a toast. The counts are the point of a bulk press, and
 * skipped rows carry a reason the operator needs to read rather than watch
 * disappear — most often that a customer supplied a PO while the screen was
 * open, which is the outcome everybody wanted.
 */
const reportOutcome = (result) => {
    const forced = result.forced ?? 0;
    const skipped = result.skipped ?? 0;
    const failed = result.failed ?? 0;
    const messages = result.messages ?? [];

    const summary = `<p class="mb-2"><strong>${forced}</strong> invoiced without a purchase order`
        + `, <strong>${skipped}</strong> skipped`
        + `, <strong>${failed}</strong> failed.</p>`;

    const detail = messages.length > 0
        ? `<ul class="text-start small mb-0">${messages.map((message) => `<li>${message}</li>`).join('')}</ul>`
        : '';

    Swal.fire({
        icon: failed > 0 ? 'error' : (skipped > 0 ? 'warning' : 'success'),
        title: 'Done',
        html: `${summary}${detail}`,
        width: '700px',
        confirmButtonText: 'Close',
    }).then(() => reload());
};

const reportError = (error) => {
    if (!error.response) {
        return;
    }

    let allErrors = '';

    if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([, messages]) => {
            messages.forEach((message) => {
                allErrors += '<p class="mb-0">' + message + '</p>';
            });
        });
    } else if (error.response?.data?.message) {
        allErrors = error.response.data.message;
    }

    Swal.fire({
        icon: 'error',
        title: 'Could not action those jobs',
        html: allErrors,
        width: '600px',
        confirmButtonText: 'Dismiss',
        confirmButtonColor: '#dc3545',
    });
};

const reload = () => {
    clearSelection();

    window.jQuery(TABLE).DataTable().ajax.reload(null, false);
};
</script>
